import React, { useState, useEffect } from 'react';
import { Icons } from './Icons';
import { readConsent, setConsent, POLICY_URL } from '../utils/consent';

/**
 * Change your mind about advertising measurement, from inside the app.
 *
 * The privacy policy promises a cookie settings link "at the foot of every
 * page", and the app is a page. That sentence is the entire reason this dialog
 * exists, so if the policy ever stops saying it, check here before deleting
 * anything.
 *
 * It is settings only, never a prompt. The decision is asked for once, on
 * kinshiptrees.com, before anything is collected. Putting a consent banner over
 * somebody's family archive to catch the few people who arrived at the app
 * directly would be a poor trade, so the app waits to be asked.
 *
 * The wording deliberately mirrors the banner on the marketing site rather than
 * paraphrasing it. Two descriptions of the same promise drift, and the one a
 * person reads second is the one they will notice differs.
 */
export default function ConsentSettingsModal({ onClose }) {
  const [granted, setGranted] = useState(() => readConsent() === 'granted');
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  // Written on click rather than on a Save button. There is one setting, the
  // effect is immediate, and a person turning something off should not have to
  // confirm that they meant it.
  const choose = (value) => {
    setConsent(value);
    setGranted(value);
    setSaved(true);
  };

  return (
    <div style={overlay} onClick={onClose}>
      <div style={modal} onClick={(e) => e.stopPropagation()} role="dialog" aria-labelledby="consent-title">
        <div style={header}>
          <h2 id="consent-title" style={titleStyle}>Cookie settings</h2>
          <button onClick={onClose} aria-label="Close" style={closeButton}>
            <Icons.Close />
          </button>
        </div>

        <div style={body}>
          <p style={para}>
            We advertise Kinship Trees on social media, currently Facebook and
            Instagram. With your permission we let the platform know about
            things like creating an account or subscribing, so we know which
            adverts are worth paying for.
          </p>
          <p style={para}>
            Nothing from your family archive is ever included. No photos, no
            recordings, no names, nothing about the people in your tree.{' '}
            <a href={POLICY_URL} target="_blank" rel="noopener noreferrer" style={link}>
              Read exactly what is and is not sent
            </a>.
          </p>

          <div style={choiceRow}>
            <button
              onClick={() => choose(true)}
              aria-pressed={granted}
              style={granted ? choiceOn : choiceOff}
            >
              Allow
            </button>
            <button
              onClick={() => choose(false)}
              aria-pressed={!granted}
              style={!granted ? choiceOn : choiceOff}
            >
              Decline
            </button>
          </div>

          <p style={stateLine} aria-live="polite">
            {granted ? 'Measurement is on.' : 'Measurement is off.'}
            {saved ? ' Saved.' : ''}
          </p>

          <p style={{ ...para, margin: '14px 0 0', fontSize: 12 }}>
            Turning this off does not change the Service in any way.
          </p>
        </div>
      </div>
    </div>
  );
}

const overlay = {
  position: 'fixed', inset: 0, background: 'rgba(44,36,24,0.5)', zIndex: 100,
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  backdropFilter: 'blur(4px)', padding: 16,
};

const modal = {
  width: 460, maxWidth: '100%', background: '#FDFBF7',
  borderRadius: 16, boxShadow: '0 20px 60px rgba(0,0,0,0.2)', overflow: 'hidden',
  fontFamily: "'DM Sans', sans-serif", display: 'flex', flexDirection: 'column',
};

const header = {
  padding: '20px 24px', borderBottom: '1px solid #E8E0D4',
  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
  background: '#F5EDE3', flexShrink: 0,
};

const titleStyle = {
  margin: 0, fontSize: 18, fontFamily: "'Crimson Pro', Georgia, serif",
  color: '#2C2418', fontWeight: 700,
};

const closeButton = {
  background: 'none', border: 'none', cursor: 'pointer', color: '#6B5E4F', padding: 4,
};

const body = { padding: 24 };

const para = { margin: '0 0 12px', fontSize: 13, color: '#6B5E4F', lineHeight: 1.6 };

const link = { color: '#9A763F', textDecoration: 'underline', textUnderlineOffset: 2 };

const choiceRow = { display: 'flex', gap: 10, marginTop: 18 };

const baseChoice = {
  flex: 1, padding: '10px 18px', borderRadius: 8, fontSize: 13, fontWeight: 600,
  fontFamily: "'DM Sans', sans-serif", cursor: 'pointer',
};

// Equal weight, same as the banner on the marketing site. Making the agreeable
// answer prettier than the refusing one is the pattern the rules exist to stop,
// and this product's whole argument is that it does not play those games.
const choiceOff = {
  ...baseChoice, border: '1px solid rgba(107,94,79,0.3)',
  background: 'transparent', color: '#6B5E4F',
};

const choiceOn = {
  ...baseChoice, border: '1px solid #2C2418',
  background: '#2C2418', color: '#FDFBF7',
};

const stateLine = { margin: '12px 0 0', fontSize: 12, color: '#8B7D6B' };
